// Shared clinical helpers used across tabs.

/** Day of life from DOB (day-of-birth = DOL 1). Returns null if DOB missing/invalid. */
export function computeDol(dobIso) {
  if (!dobIso) return null;
  try {
    const dob = new Date(dobIso);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    const dobUTC = Date.UTC(dob.getFullYear(), dob.getMonth(), dob.getDate());
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.floor((todayUTC - dobUTC) / 86400000);
    return Math.max(days + 1, 1);
  } catch { return null; }
}

/** Prefer computed DOL from DOB, fall back to stored value. */
export function patientDol(patient) {
  const c = computeDol(patient?.dob);
  return c != null ? c : (patient?.day_of_life ?? null);
}

/** Map a dose-frequency string to hours between doses.
 *  Returns null if the drug is a one-off ("stat") or the frequency is unrecognised. */
export function frequencyHours(freq) {
  if (!freq) return null;
  const f = String(freq).toLowerCase().trim();
  if (f === "stat" || f === "once" || f === "prn") return null;
  const table = [
    ["q1h", 1], ["q2h", 2], ["q3h", 3], ["q4h", 4], ["q6h", 6],
    ["q8h", 8], ["q12h", 12], ["q24h", 24],
    ["qid", 6], ["tds", 8], ["tid", 8], ["bd", 12], ["bid", 12], ["od", 24], ["hs", 24],
  ];
  for (const [k, h] of table) { if (f.includes(k)) return h; }
  const m = f.match(/q(\d+)h/);
  if (m) return Number(m[1]);
  return null;
}

/** Compute the next dose time for an order given its last administration.
 *  Returns { at: Date, hoursLeft: number, overdue: boolean } or null if not scheduled. */
export function nextDoseAt(order, lastAdmin) {
  const hours = frequencyHours(order?.frequency);
  if (!hours) return null;
  const baseIso = lastAdmin?.given_at || order?.start_at || order?.created_at;
  if (!baseIso) return null;
  try {
    const base = new Date(baseIso);
    if (isNaN(base.getTime())) return null;
    const next = new Date(base.getTime() + hours * 3600_000);
    const diffMs = next.getTime() - Date.now();
    const hoursLeft = diffMs / 3_600_000;
    return { at: next, hoursLeft, overdue: hoursLeft < 0 };
  } catch { return null; }
}

/** Format a duration between two Date-ables as "3 h 12 min" / "2 d 5 h". */
export function humanDurationBetween(fromIso, toIso = null) {
  if (!fromIso) return "—";
  try {
    const from = new Date(fromIso);
    const to = toIso ? new Date(toIso) : new Date();
    let sec = Math.max(0, Math.floor((to.getTime() - from.getTime()) / 1000));
    const d = Math.floor(sec / 86400); sec -= d * 86400;
    const h = Math.floor(sec / 3600); sec -= h * 3600;
    const m = Math.floor(sec / 60);
    if (d) return `${d} d ${h} h`;
    if (h) return `${h} h ${m} min`;
    if (m) return `${m} min`;
    return "just now";
  } catch { return "—"; }
}

/** Short relative label like "in 2 h 15 min" / "overdue by 45 min". */
export function humanRelative(diffHours) {
  if (diffHours == null || !isFinite(diffHours)) return "—";
  const abs = Math.abs(diffHours);
  const totalMin = Math.round(abs * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const parts = [];
  if (h) parts.push(`${h} h`);
  if (m || !h) parts.push(`${m} min`);
  const body = parts.join(" ");
  return diffHours < 0 ? `overdue by ${body}` : `in ${body}`;
}

export const STANDARD_INVESTIGATIONS = [
  "CBC", "CRP", "ABG", "Electrolytes", "LFT", "RFT",
  "Blood Culture", "Urine Culture", "Chest X-ray", "USG",
  "CT", "MRI", "Bilirubin", "Serum Bilirubin", "Blood Sugar",
  "2D Echocardiography",
];

/** Format an ISO date-time in the user's local timezone (IST for Indian users) as
 *  "23 Feb, 14:35". Returns "—" for null/invalid. */
export function fmtLocalDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    return d.toLocaleString([], {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return String(iso).slice(0, 16).replace("T", " "); }
}

/** Longer variant "23 Feb 2026, 14:35:07" for audit-style rows. */
export function fmtLocalDTFull(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    return d.toLocaleString([], {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  } catch { return String(iso).slice(0, 19).replace("T", " "); }
}

/** Time-only "14:35" in local tz. */
export function fmtLocalTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return "—"; }
}
